/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { identity, pick, pickBy } from 'lodash';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { TFunction } from 'react-i18next';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import {
  CrudAddType,
  CrudEditType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  ListColumns
} from 'app/components/organisms/crud/form-types';
import { formFields } from './form-fields';
import { SubscriptionTeam } from 'app/types/subscription';
import moment from 'moment';
import { RenderUserItemList } from './render-user-item';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<SubscriptionTeam> => [
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'name',
    key: 'name',
    size: '1fr',
    render: item => <RenderUserItemList item={item.user} />
  },
  {
    title: translate('common.table.columns.pharmacy'),
    dataIndex: 'pharmacy',
    key: 'pharmacy',
    size: '1fr',
    render: item => item.pharmacy?.name
  },
  {
    title: translate('common.table.columns.role'),
    dataIndex: 'role',
    key: 'role',
    render: value => value.role
  },
  {
    title: translate('common.table.columns.active'),
    dataIndex: 'active',
    key: 'active',
    render: value => (!value.blocked ? '✅' : '❌')
  },
  {
    title: translate('common.table.columns.confirmed'),
    dataIndex: 'confirmed',
    key: 'confirmed',
    render: value => (value.confirmed ? '✅' : '❌')
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: value => <DateTime date={value.createdAt} />
  },
  {
    title: translate('common.table.columns.updatedAt'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    render: value => <DateTime date={value.updatedAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  }
];

/**
 * List.
 */

function list(translate: TFunction): CrudListType<SubscriptionTeam> {
  return {
    columns: listColumns(translate),
    filters: listFilters(translate),
    route: appRoutes.dashboard.services.subscriptionsDetail.users,
    enableExport: true,
    addButton: translate('common.actions.add'),
    canEdit: true,
    canRemove: true,
    resolveParams: (params: any, item: any) => {
      return {
        subscriptionId: params?.subscriptionId,
        userId: params?.userId ?? item?.userId,
        pharmacyId: params?.pharmacyId ?? item?.pharmacyId
      };
    },
    key: ['userPinNumbers'],
    normalizeFilters: (data: any) => {
      const { ...filters } = data.filters;
      const result: any = {
        filters: pickBy(filters, identity),
        order: []
      };

      return result;
    },
    normalizeData: (result: any) => {
      return result;
    },
    pagination: {
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100, 500, 1000]
    },
    endpoint: apiEndpoints.subscriptionsUsers,
    endpointById: apiEndpoints.subscriptionsUsersById
  };
}

/**
 * Remove.
 */

const remove = (): CrudRemoveType<SubscriptionTeam> => ({
  endpoint: apiEndpoints.subscriptionsUsersById,
  resolveParams: (params: any, item?: SubscriptionTeam) => {
    return {
      subscriptionId: params?.subscriptionId,
      userId: params?.userId ?? item?.userId,
      pharmacyId: params?.pharmacyId ?? item?.pharmacyId
    };
  }
});

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType<SubscriptionTeam> => ({
  formFields: formFields(translate, 'add')
});

/**
 * Edit.
 */

const edit = (translate: TFunction): CrudEditType<SubscriptionTeam> => ({
  formFields: formFields(translate, 'edit'),
  endpoint: apiEndpoints.subscriptionsUsersById,
  redirect: appRoutes.dashboard.services.subscriptionsList,
  resolveParams: (params: any) => {
    return {
      subscriptionId: params?.subscriptionId,
      id: params?.id
    };
  },
  route: appRoutes.dashboard.services.subscriptionsDetail.usersEdit,
  submitLabel: translate('common.actions.edit'),
  normalizePayload(values) {
    const result = { ...values };

    delete result.userId;
    delete result.pharmacyId;
    delete result.subscriptionId;
    delete result.createdAt;
    delete result.updatedAt;

    return result;
  },
  normalizeInitialValues: (keys: string[], payload: SubscriptionTeam) => {
    const data: any = pick(payload, keys);

    data.createdAt = moment(data.createdAt).format('YYYY-MM-DD HH:mm:ss');
    data.updatedAt = moment(data.updatedAt).format('YYYY-MM-DD HH:mm:ss');

    return data;
  }
});

/**
 * Config.
 */

export function createSubscriptionsDetailUsersTemplate(translate: TFunction): CrudTemplate<SubscriptionTeam> {
  return normalizeTemplate(translate, {
    list: list(translate),
    edit: edit(translate),
    remove: remove(),
    add: add(translate)
  });
}
